import React, { useMemo, useState, useRef, useEffect } from 'react';
import type { ProductType, GlassTypeCategory, GlassValueDropdownOption } from '../../utils/types';
import { useGetGlassValuesDropdownQuery } from '../../store/slices/apiSlice';

interface Props {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  productType?: ProductType;
  glassValue?: GlassTypeCategory;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  className?: string;
}

export const SearchableGlassValueSelect: React.FC<Props> = ({
  value,
  onChange,
  productType,
  glassValue,
  placeholder = 'Search and select…',
  disabled,
  label,
  required,
  className,
}) => {
  const { data, isFetching } = useGetGlassValuesDropdownQuery(
    { product_type: productType, glass_value: glassValue }
  );
  
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const options: GlassValueDropdownOption[] = useMemo(() => {
    const list = data?.results ?? [];
    if (!search.trim()) return list;
    const s = search.toLowerCase();
    return list.filter(o => o.label.toLowerCase().includes(s));
  }, [data, search]);

  // Update selected label when value changes
  useEffect(() => {
    if (value) {
      const option = data?.results?.find(o => o.value === value);
      setSelectedLabel(option?.label || '');
    } else {
      setSelectedLabel('');
    }
  }, [value, data]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: GlassValueDropdownOption) => {
    onChange(option.value);
    setSelectedLabel(option.label);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className={className} ref={containerRef}>
      {label && (
        <label className="form-label">
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          className="form-input w-full cursor-pointer"
          placeholder={selectedLabel || placeholder}
          value={isOpen ? search : selectedLabel}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
          readOnly={!isOpen}
        />
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            <div className="p-1">
              <div 
                className="px-3 py-2 text-sm text-gray-500 cursor-pointer hover:bg-gray-100"
                onClick={() => { onChange(null); setSelectedLabel(''); setIsOpen(false); setSearch(''); }}
              >
                Clear selection
              </div>
              {options.map(option => {
                // Style based on glass value type: light gray for cutting, white for arrival
                const baseClasses = 'px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-900';
                const backgroundClass = option.is_cutting ? 'bg-gray-100' : 'bg-white';
                
                return (
                  <div
                    key={option.value}
                    className={`${baseClasses} ${backgroundClass}`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </div>
                );
              })}
              {options.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {isFetching ? 'Loading...' : 'No matching glass values found'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


